<svelte:options runes={false} />

<script>
  import { dollarsToMinor, isInvoicePayable } from '../../api/invoices';
  import LedgerHeader from '../components/LedgerHeader.svelte';
  import NoticeBanner from '../components/NoticeBanner.svelte';
  import PayerContributionForm from '../components/PayerContributionForm.svelte';
  import PayerHourBitCard from '../components/PayerHourBitCard.svelte';
  import RouteState from '../components/RouteState.svelte';
  import SuggestedQuotePanel from '../components/SuggestedQuotePanel.svelte';

  export let invoice;
  export let operatorAccount;
  export let quote;
  export let suggestedQuote;
  export let payerQuoteDiffersFromSuggestion;
  export let hourBits;
  export let bitRates;
  export let selectedBitIds;
  export let hourlyRate;
  export let flatAddition;
  export let flatAdditionReason;
  export let payerEmail;
  export let payerName;
  export let payerCompany;
  export let providerId;
  export let availableProviders;
  export let selectedProvider;
  export let payerFeeQuoteAvailable;
  export let selectedHours;
  export let payerBlockers;
  export let ledgerAggregate = null;
  export let onToggleBit;
  export let onCreateIntent;

  // U04 moved this guarantee into the bearer projection: unreported bits are no
  // longer published at all, so this filter now has nothing to remove. It stays
  // as a redundant second line, not as the guarantee.
  $: payableBits = hourBits.filter((bit) => bit.reported);
  $: reportedHours = payableBits.reduce((sum, bit) => sum + (bit.hours || 0), 0);
  $: selectedCount = payableBits.filter((bit) => selectedBitIds.includes(bit._id)).length;
</script>

<section class="panel pay-panel payer-shell">
  <LedgerHeader
    {invoice}
    operatorName={operatorAccount?.name}
    {reportedHours}
    {ledgerAggregate}
  />

  {#if invoice.archivedAt}
    <NoticeBanner>
      This page is no longer listed publicly, but you can still pay for this work.
    </NoticeBanner>
  {:else if !isInvoicePayable(invoice)}
    <NoticeBanner>
      This page was closed by {operatorAccount?.name || 'the person who did the work'}
      and is no longer taking payments.
    </NoticeBanner>
  {/if}

  {#if isInvoicePayable(invoice)}
    <section class="payer-step" aria-labelledby="payer-work-heading">
      <p class="step-label">
        Step 1 · The work
        {#if selectedCount > 0}
          <span class="step-count">{selectedCount} of {payableBits.length} chosen</span>
        {/if}
      </p>
      <h3 id="payer-work-heading">What do you want to pay for?</h3>

      {#if payableBits.length === 0}
        <!-- Empty renders in place: the ledger header above already says whose
             page this is, and replacing the route would take that away. -->
        <RouteState routeKey="invoice" state="empty" bare />
      {:else}
        <div class="bit-list">
          {#each payableBits as bit (bit._id)}
            <PayerHourBitCard
              {bit}
              currency={invoice.currency}
              rates={bitRates(bit)}
              hourlyRateMinor={dollarsToMinor(hourlyRate)}
              selected={selectedBitIds.includes(bit._id)}
              onToggle={() => onToggleBit(bit._id)}
            />
          {/each}
        </div>
      {/if}

      <SuggestedQuotePanel
        currency={invoice.currency}
        suggestedTotalMinor={suggestedQuote.chargeTotalMinor}
        showCurrentContribution={payerQuoteDiffersFromSuggestion}
        currentTotalMinor={quote.chargeTotalMinor}
      />
    </section>

    <PayerContributionForm
      bind:hourlyRate
      bind:flatAddition
      bind:flatAdditionReason
      bind:payerEmail
      bind:payerName
      bind:payerCompany
      bind:providerId
      {availableProviders}
      {selectedProvider}
      {quote}
      currency={invoice.currency}
      {selectedHours}
      {payerFeeQuoteAvailable}
      minHourlyRateMinor={invoice.minHourlyRateMinor}
      suggestedAtMinimumMinor={suggestedQuote.chargeTotalMinor}
      {reportedHours}
      {ledgerAggregate}
      blockers={payerBlockers}
      onSubmit={onCreateIntent}
    />
  {/if}
</section>
